/* eslint import/no-unresolved: 0 */
import { useState, useEffect } from 'react';
import { useModel } from '@src/generic/model-store';

const CLICK_COUNT_KEY = 'VTR_IN_COURSE_MODAL_NEXT_CLICK_COUNT';
const SHOW_MODAL_CLICK_COUNT = 3;

function isLocalStorageAvailable() {
  // A simple helper to check if localStorage is currently available
  const testKey = 'testKey';
  try {
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

function useClickCount() {
  /**
   * Hook that tracks the number of times that a user has clicked something
   * and stores that number in localStorage (if available)
   *
   * Returns: [clickCount, setClickCount]
   *  - clickCount (int): The current count of clicks
   *  - setClickCount (func): Function to update the number of clicks
   */
  const localStorageAvailable = isLocalStorageAvailable();
  const [clickCount, setClickCount] = useState(() => {
    if (!localStorageAvailable) { return 0; }
    const clickString = localStorage.getItem(CLICK_COUNT_KEY);
    const clicks = JSON.parse(clickString);
    return clicks || 0;
  });

  useEffect(() => {
    // When clickCount changes, this will update it in localStorage
    if (clickCount === undefined) { return; }
    if (!localStorageAvailable) { return; }
    localStorage.setItem(CLICK_COUNT_KEY, JSON.stringify(clickCount));
  }, [clickCount, localStorageAvailable]);

  return [clickCount, setClickCount];
}

function useInCourseUpgradeModal() {
  /**
   * Hook that wraps around useClickCount to handle the "modal show" logic
  */
  const [clickCount, setClickCount] = useClickCount();
  const nextClick = clickCount + 1;
  const countNextClick = () => { setClickCount(nextClick); };
  const shouldShowModalOnClick = nextClick % SHOW_MODAL_CLICK_COUNT === 0;
  return [shouldShowModalOnClick, countNextClick];
}

function useIsModalEnabled(courseId) {
  /** The following must be true for the experiment to be activated:
   * - The user must be an audit learner
   * - The user must not be staff
   * - The course must have a verified mode
   * - (During rollout) The org must be whitelisted
  */
  const {
    verifiedMode,
    isStaff,
  } = useModel('courseHomeMeta', courseId);
  const { enrollmentMode } = useModel('coursewareMeta', courseId);

  return enrollmentMode === 'audit' && !!verifiedMode && !isStaff;
}

export {
  useInCourseUpgradeModal,
  useIsModalEnabled,
};
